import { Button, Checkbox, Input, Textarea } from "@mantine/core";
import {
  IconBriefcase,
  IconBuilding,
  IconFileText,
  IconHash,
  IconId,
  IconMail,
  IconMapPin,
  IconPhone,
  IconPlus,
  IconPrinter,
  IconTrash,
  IconUser,
  IconUsers,
} from "@tabler/icons-react";

import { notification } from "antd";
import { orpcClient } from "@/lib/orpc-client";
import { single } from "@/lib/orpc-batch";
import { useData } from "@/utility/state/contextAPI";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { useTranslation } from "react-i18next";

interface AddressData {
  city?: string;
  district?: string;
  postalCode?: number;
  address?: string;
}

interface PartnerContact {
  name: string;
  department: string;
  telephone: string;
  email: string;
}

interface CoopData {
  name: string;
  abbreviation: string;
  businessIdNumber: string | null;
  businessAddress: AddressData;
  invoiceAddress: AddressData;
  shippingAddress: AddressData;
  invoiceType: string;
  business: string;
  contacts: PartnerContact[];
  telephone: string;
  fax: string;
}

function AddCoopListPage() {
  const { t } = useTranslation();
  const [currentStage, setCurrentStage] = useState(0);
  const [coopData, setCoopData] = useState<CoopData>({
    name: "",
    abbreviation: "",
    businessIdNumber: null,
    businessAddress: {},
    invoiceAddress: {},
    shippingAddress: {},
    invoiceType: "",
    business: "",
    contacts: [],
    telephone: "",
    fax: "",
  });
  const [invoiceDisabled, setInvoiceDisabled] = useState(false);
  const [shippingAddressDisabled, setShippingAddressDisabled] = useState(false);
  const [shippingAsInvoiceDisabled, setShippingAsInvoiceDisabled] =
    useState(false);
  const {
    modal: { contextOpenModal, contextCloseModal, contextSetModalContent },
  } = useData();
  const navigate = useNavigate();
  const [api, contextHolder] = notification.useNotification();

  const handleSubmit = async (coopData: CoopData) => {
    try {
      const result = await single(
        orpcClient.partner.create([
          {
            ...(coopData as any),
            invoiceAddress: (invoiceDisabled
              ? coopData?.businessAddress
              : coopData?.invoiceAddress) as any,
            shippingAddress: (shippingAddressDisabled
              ? coopData?.businessAddress
              : shippingAsInvoiceDisabled
                ? invoiceDisabled
                  ? coopData?.businessAddress
                  : coopData?.invoiceAddress
                : coopData?.shippingAddress) as any,
          },
        ])
      );
      if (result) {
        setCurrentStage(1);
      }
    } catch (error: any) {
      api.error({
        message: `Notification `,
        description: <div>{error?.message || "Error creating partner"}</div>,
        placement: "topLeft",
        duration: 0,
      });
      console.error("partner.create error", error);
    }
  };

  const handleDataChange = (name: keyof CoopData, value: string) => {
    setCoopData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleChangeAddressData = (
    value: string,
    addressType: keyof Pick<
      CoopData,
      "businessAddress" | "invoiceAddress" | "shippingAddress"
    >,
    type: keyof AddressData
  ) => {
    const processedValue =
      type === "postalCode" ? Number(value) || undefined : value;
    setCoopData((prevData) => ({
      ...prevData,
      [addressType]: {
        ...prevData[addressType],
        [type]: processedValue,
      },
    }));
  };

  const handleAddContact = () => {
    setCoopData((prevData) => ({
      ...prevData,
      contacts: [
        ...prevData.contacts,
        { name: "", department: "", telephone: "", email: "" },
      ],
    }));
  };

  const handleRemoveContact = (index: number) => {
    setCoopData((prevData) => ({
      ...prevData,
      contacts: prevData.contacts.filter((_, i) => i !== index),
    }));
  };

  const handleChangeContactField = (
    index: number,
    field: keyof PartnerContact,
    value: string
  ) => {
    setCoopData((prevData) => ({
      ...prevData,
      contacts: prevData.contacts.map((contact, i) =>
        i === index ? { ...contact, [field]: value } : contact
      ),
    }));
  };

  const {
    name,
    abbreviation,
    businessIdNumber,
    invoiceType,
    business,
    contacts,
    telephone,
    fax,
  } = coopData;

  const renderControl = (
    label: React.ReactNode,
    control: React.ReactNode,
    Icon?: React.ComponentType<{ size?: number | string; className?: string }>
  ) => (
    <div className="space-y-1.5">
      <label className="flex items-center gap-1.5 text-xs text-gray-500">
        {Icon ? <Icon size={13} /> : null}
        {label}
      </label>
      {control}
    </div>
  );

  const renderAddressGrid = (
    addressType: keyof Pick<
      CoopData,
      "businessAddress" | "invoiceAddress" | "shippingAddress"
    >,
    disabled?: boolean
  ) => (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {renderControl(
        <>{t("coop.city")}＊</>,
        <Input
          disabled={disabled}
          onChange={(e) =>
            handleChangeAddressData(e.target.value, addressType, "city")
          }
          leftSection={<IconMapPin size={16} />}
        />,
        IconMapPin
      )}
      {renderControl(
        <>{t("coop.district")}＊</>,
        <Input
          disabled={disabled}
          onChange={(e) =>
            handleChangeAddressData(e.target.value, addressType, "district")
          }
          leftSection={<IconMapPin size={16} />}
        />,
        IconMapPin
      )}
      {renderControl(
        <>{t("coop.postalCode")}＊</>,
        <Input
          disabled={disabled}
          onChange={(e) =>
            handleChangeAddressData(e.target.value, addressType, "postalCode")
          }
          leftSection={<IconHash size={16} />}
        />,
        IconHash
      )}
      {renderControl(
        <>{t("coop.address")}＊</>,
        <Input
          disabled={disabled}
          onChange={(e) =>
            handleChangeAddressData(e.target.value, addressType, "address")
          }
          leftSection={<IconMapPin size={16} />}
        />,
        IconMapPin
      )}
    </div>
  );

  return currentStage === 0 ? (
    <div className="min-h-screen p-8">
      {contextHolder}
      <div className="mx-auto max-w-4xl rounded-xl bg-white p-8 space-y-5">
        <div>
          <div className="text-4xl font-bold" style={{ color: "#6F6F6F" }}>
            {t("coop.add")}
          </div>
          <div className="mt-2 text-sm text-gray-500">
            {t("coop.addNew") || "新增合作方"}
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
            <IconFileText size={16} className="text-blue-600" />
            <h3 className="text-sm font-medium text-gray-700">
              {t("coop.basicInfo")}
            </h3>
          </div>
          <div className="grid grid-cols-1 gap-4 p-6 md:grid-cols-2 lg:grid-cols-3">
            {renderControl(
              t("coop.companyName") || "公司名",
              <Input
                value={name}
                onChange={({ target: { value } }) =>
                  handleDataChange("name", value)
                }
                leftSection={<IconBuilding size={16} />}
              />,
              IconBuilding
            )}
            {renderControl(
              t("coop.abbreviation"),
              <Input
                value={abbreviation}
                onChange={({ target: { value } }) =>
                  handleDataChange("abbreviation", value)
                }
                leftSection={<IconBuilding size={16} />}
              />,
              IconBuilding
            )}
            {renderControl(
              <>{t("coop.businessIdNumber") || "統編"}＊</>,
              <Input
                value={businessIdNumber || ""}
                onChange={({ target: { value } }) =>
                  handleDataChange("businessIdNumber", value)
                }
                leftSection={<IconId size={16} />}
              />,
              IconId
            )}
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
            <IconMapPin size={16} className="text-blue-600" />
            <h3 className="text-sm font-medium text-gray-700">
              {t("coop.contactAddress")}
            </h3>
          </div>
          <div className="space-y-6 p-6">
            <div className="space-y-3">
              <div
                className="text-sm font-medium"
                style={{ color: "#6F6F6F" }}
              >
                {t("coop.businessRegisterAddress")}
              </div>
              {renderAddressGrid("businessAddress")}
            </div>

            <div className="space-y-3">
              <div
                className="text-sm font-medium"
                style={{ color: "#6F6F6F" }}
              >
                {t("coop.invoiceAddressSection")}
              </div>
              {renderAddressGrid("invoiceAddress", invoiceDisabled)}
              <Checkbox
                checked={invoiceDisabled}
                onChange={() => setInvoiceDisabled((prev) => !prev)}
                label={t("coop.sameAsBusinessAddress")}
                color="gray"
              />
            </div>

            <div className="space-y-3">
              <div
                className="text-sm font-medium"
                style={{ color: "#6F6F6F" }}
              >
                {t("coop.shippingAddressSection")}
              </div>
              {renderAddressGrid(
                "shippingAddress",
                shippingAddressDisabled || shippingAsInvoiceDisabled
              )}
              <div className="flex items-center gap-4">
                <Checkbox
                  label={t("coop.sameAsBusinessAddress")}
                  color="gray"
                  checked={shippingAddressDisabled}
                  onChange={() =>
                    setShippingAddressDisabled((prev) => {
                      if (!prev) setShippingAsInvoiceDisabled(false);
                      return !prev;
                    })
                  }
                />
                <Checkbox
                  label={t("coop.sameAsInvoiceAddress")}
                  color="gray"
                  checked={shippingAsInvoiceDisabled}
                  onChange={() =>
                    setShippingAsInvoiceDisabled((prev) => {
                      if (!prev) setShippingAddressDisabled(false);
                      return !prev;
                    })
                  }
                />
              </div>
            </div>
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-6 py-4">
            <div className="flex items-center gap-2">
              <IconUsers size={16} className="text-blue-600" />
              <h3 className="text-sm font-medium text-gray-700">
                {t("coop.contactInfo")}
              </h3>
            </div>
            <Button
              variant="default"
              radius="md"
              size="sm"
              leftSection={<IconPlus size={14} />}
              onClick={handleAddContact}
            >
              {t("coop.addContact") || "新增聯絡人"}
            </Button>
          </div>
          <div className="space-y-6 p-6">
            <div className="space-y-3">
              <div
                className="text-sm font-medium"
                style={{ color: "#6F6F6F" }}
              >
                {t("coop.basicContactInfo")}
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {renderControl(
                  t("coop.phone"),
                  <Input
                    leftSection={<IconPhone size={16} />}
                    onChange={({ target: { value } }) =>
                      handleDataChange("telephone", value)
                    }
                    value={telephone}
                  />,
                  IconPhone
                )}
                {renderControl(
                  t("coop.fax"),
                  <Input
                    leftSection={<IconPrinter size={16} />}
                    onChange={({ target: { value } }) =>
                      handleDataChange("fax", value)
                    }
                    value={fax}
                  />,
                  IconPrinter
                )}
                {renderControl(
                  t("coop.invoiceType"),
                  <Input
                    leftSection={<IconFileText size={16} />}
                    value={invoiceType}
                    onChange={({ target: { value } }) =>
                      handleDataChange("invoiceType", value)
                    }
                  />,
                  IconFileText
                )}
              </div>
            </div>

            <div className="space-y-3">
              <div
                className="text-sm font-medium"
                style={{ color: "#6F6F6F" }}
              >
                {t("coop.contactPersonInfo")}
              </div>
              {contacts.length === 0 ? (
                <div className="text-sm text-gray-400">
                  {t("coop.noContacts") || "尚未新增聯絡人"}
                </div>
              ) : (
                contacts.map((contact, index) => (
                  <div key={index} className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div
                        className="text-sm font-medium"
                        style={{ color: "#6F6F6F" }}
                      >
                        {t("coop.contactNumber", { number: index + 1 }) ||
                          `聯絡人 #${index + 1}`}
                      </div>
                      <Button
                        variant="subtle"
                        color="red"
                        size="sm"
                        radius="md"
                        leftSection={<IconTrash size={14} />}
                        onClick={() => handleRemoveContact(index)}
                      >
                        {t("coop.removeContact") || "移除聯絡人"}
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                      {renderControl(
                        t("coop.contactPerson") || "聯絡人",
                        <Input
                          leftSection={<IconUser size={16} />}
                          value={contact.name}
                          onChange={({ target: { value } }) =>
                            handleChangeContactField(index, "name", value)
                          }
                        />,
                        IconUser
                      )}
                      {renderControl(
                        t("coop.department"),
                        <Input
                          leftSection={<IconBuilding size={16} />}
                          value={contact.department}
                          onChange={({ target: { value } }) =>
                            handleChangeContactField(index, "department", value)
                          }
                        />,
                        IconBuilding
                      )}
                      {renderControl(
                        t("coop.phone") || "電話",
                        <Input
                          leftSection={<IconPhone size={16} />}
                          value={contact.telephone}
                          onChange={({ target: { value } }) =>
                            handleChangeContactField(index, "telephone", value)
                          }
                        />,
                        IconPhone
                      )}
                      {renderControl(
                        t("coop.email") || "E-mail",
                        <Input
                          leftSection={<IconMail size={16} />}
                          value={contact.email}
                          onChange={({ target: { value } }) =>
                            handleChangeContactField(index, "email", value)
                          }
                        />,
                        IconMail
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
            <IconBriefcase size={16} className="text-blue-600" />
            <h3 className="text-sm font-medium text-gray-700">
              {t("coop.businessType")}
            </h3>
          </div>
          <div className="p-6">
            <Textarea
              value={business}
              onChange={({ target: { value } }) =>
                handleDataChange("business", value)
              }
            />
          </div>
        </div>

        <div className="flex gap-4">
          <Button
            variant="default"
            style={{ flex: 1 }}
            radius="md"
            onClick={() => navigate("/hrMainPage/coop")}
          >
            {t("coop.cancel")}
          </Button>
          <Button
            variant="filled"
            color="gray"
            style={{ flex: 1 }}
            radius="md"
            onClick={() => handleSubmit(coopData)}
          >
            {t("coop.addBtn")}
          </Button>
        </div>
      </div>
    </div>
  ) : (
    <div className="flex min-h-screen h-full items-center justify-center p-14 text-center">
      <div className="w-[487px] rounded-xl bg-white p-8">
        <div style={{ color: "#6F6F6F" }}>{t("coop.createSuccess")}</div>
        <Button
          variant="filled"
          color="gray"
          style={{ width: "100%", margin: "1rem 0" }}
          radius="md"
          onClick={() => navigate("/hrMainPage/coop")}
        >
          {t("coop.confirm")}
        </Button>
      </div>
    </div>
  );
}
export default AddCoopListPage;
